import React, { useEffect, useState } from 'react';
import { Briefcase, BadgeCheck, Clock, XCircle, Loader2, Percent, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { accountAPI } from '../services/api';
import { useT } from '../services/i18n';

const errText = (e, fb) => { const d = e.response?.data?.detail; return Array.isArray(d) ? d.map(x => x.msg).join(' ') : d || fb; };
const STATUS = {
  pending: { label: 'acc.proPending', icon: Clock, cls: 'bg-amber-50 border-amber-200 text-amber-800' },
  validated: { label: 'acc.proValidated', icon: BadgeCheck, cls: 'bg-green-50 border-green-200 text-green-800' },
  refused: { label: 'acc.proRefused', icon: XCircle, cls: 'bg-red-50 border-red-200 text-red-800' },
};

// Professional account request (validated by the admin)
const ProAccountSection = ({ onChanged }) => {
  const { t } = useT();
  const [pro, setPro] = useState(undefined);
  const [sectors, setSectors] = useState([]);
  const [form, setForm] = useState({ company_name: '', siret: '', vat_number: '', sector: '', document_url: '', phone: '' });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([accountAPI.pro(), accountAPI.proSectors()]).then(([p, s]) => { setPro(p); setSectors(s); if (p) setForm({ company_name: p.company_name, siret: p.siret, vat_number: p.vat_number, sector: p.sector, document_url: p.document_url || '', phone: p.phone || '' }); else setForm(f => ({ ...f, sector: s[0] || '' })); }).catch(() => setPro(null));
  }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    setSaving(true);
    try { const p = await accountAPI.requestPro(form); setPro(p); setEditing(false); toast.success('Demande envoyée — notre équipe la valide sous 24 h ouvrées'); onChanged && onChanged(); } catch (e) { toast.error(errText(e, 'Envoi impossible')); } finally { setSaving(false); }
  };
  const cancel = async () => {
    if (!window.confirm('Retirer votre demande / statut professionnel ?')) return;
    await accountAPI.cancelPro(); setPro(null); setEditing(false); toast.success('Demande retirée'); onChanged && onChanged();
  };

  if (pro === undefined) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-orange-500" /></div>;
  const st = pro ? STATUS[pro.status] : null;
  const showForm = !pro || editing;
  return (
    <div className="space-y-6" data-testid="pro-section">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-1"><Briefcase className="text-orange-500" size={20} /> {t('acc.pro')}</h2>
        <p className="text-sm text-gray-500 mb-4">{t('acc.proInfo')}</p>
        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm mb-5">
          {[[t('acc.proHt'), Percent], [t('acc.proInvoices'), FileText], [t('acc.proBadge'), BadgeCheck]].map(([label, I]) => <li key={label} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 text-gray-700"><I size={16} className="text-orange-500" /> {label}</li>)}
        </ul>

        {st && (
          <div className={`border rounded-lg p-4 flex items-start gap-3 ${st.cls}`} data-testid={`pro-status-${pro.status}`}>
            <st.icon size={22} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-semibold">{t(st.label)}</p>
              <p className="mt-0.5">{pro.company_name} · SIRET {pro.siret}{pro.vat_number ? ` · TVA ${pro.vat_number}` : ''} · {pro.sector}</p>
              {pro.review_note && <p className="mt-1 italic" data-testid="pro-review-note">Message de notre équipe : « {pro.review_note} »</p>}
              <p className="text-xs opacity-70 mt-1">Demande du {new Date(pro.requested_at).toLocaleDateString('fr-FR')}{pro.reviewed_at ? ` · traitée le ${new Date(pro.reviewed_at).toLocaleDateString('fr-FR')}` : ''}</p>
              <div className="flex gap-3 mt-3">
                {!editing && <button type="button" onClick={() => setEditing(true)} data-testid="pro-edit" className="text-xs underline">{t('acc.editInfo')}</button>}
                <button type="button" onClick={cancel} data-testid="pro-cancel" className="text-xs underline">{t('acc.withdraw')}</button>
              </div>
            </div>
          </div>
        )}

        {showForm && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5" data-testid="pro-form">
            <div className="sm:col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">{t('acc.companyName')}</label><Input data-testid="pro-company" value={form.company_name} onChange={set('company_name')} placeholder="Garage Martin SARL" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('acc.siret')}</label><Input data-testid="pro-siret" value={form.siret} onChange={set('siret')} placeholder="123 456 789 00012" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('acc.vatNumber')}</label><Input data-testid="pro-vat" value={form.vat_number} onChange={set('vat_number')} placeholder="FR12345678901" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('acc.sector')}</label>
              <select data-testid="pro-sector" value={form.sector} onChange={set('sector')} className="w-full h-10 border border-gray-300 rounded-md px-3 text-sm bg-white">{sectors.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">{t('acc.companyPhone')}</label><Input data-testid="pro-phone" value={form.phone} onChange={set('phone')} placeholder="01 23 45 67 89" /></div>
            <div className="sm:col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">{t('acc.proof')}</label><Input data-testid="pro-document" value={form.document_url} onChange={set('document_url')} placeholder="https://…" /></div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              {pro && <Button variant="outline" onClick={() => setEditing(false)} data-testid="pro-form-cancel">{t('common.cancel')}</Button>}
              <Button onClick={submit} disabled={saving || !form.company_name || !form.siret} data-testid="pro-submit" className="bg-[#1e3a5f] hover:bg-[#2d4a6f]">{saving && <Loader2 size={16} className="mr-2 animate-spin" />} {pro ? t('acc.resend') : t('acc.requestPro')}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProAccountSection;
